import { UsersApiAdapter } from '../adapters/UsersApiAdapter';
import {
  UserModel,
  Filters,
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  InvitationInfo,
  PendingInvitation,
} from '../models/User';

export class Service {
  constructor(private apiAdapter: UsersApiAdapter) {}

  /**
   * Obtener todos los usuarios
   */
  async getAll(_filters: Filters = {}): Promise<UserModel[]> {
    return await this.apiAdapter.getAll();
  }

  /**
   * Obtener información sobre una invitación
   * @param token - Token de la invitación
   * @returns Promise con información de la invitación
   */
  async getInvitationInfo(token: string): Promise<InvitationInfo> {
    if (!token || !token.trim()) {
      throw new Error('El token es requerido');
    }

    try {
      return await this.apiAdapter.getInvitationInfo(token.trim());
    } catch (error: any) {
      // Transformar errores de API a errores de negocio
      if (error.response?.status === 404) {
        throw new Error('Invitación no encontrada');
      }
      if (error.response?.status === 400) {
        throw new Error('Invitación inválida o expirada');
      }
      throw error;
    }
  }

  /**
   * Aceptar una invitación de usuario al equipo
   * @param request - Datos de la invitación (token y password)
   * @returns Promise con token JWT para autenticación
   */
  async acceptInvitation(request: AcceptInvitationRequest): Promise<AcceptInvitationResponse> {
    // Validaciones de negocio
    if (!request.token || !request.token.trim()) {
      throw new Error('El token es requerido');
    }

    // Solo validar name si viene en el request (puede ser opcional si user_exists es true)
    if (request.name && request.name.trim().length === 0) {
      throw new Error('El nombre no puede estar vacío');
    }

    // Solo validar password si viene en el request (puede ser opcional si user_exists es true)
    if (request.password) {
      if (request.password.trim().length < 8) {
        throw new Error('La contraseña debe tener al menos 8 caracteres');
      }
    }

    try {
      return await this.apiAdapter.acceptInvitation(
        request.token.trim(),
        request.name?.trim() || '',
        request.password?.trim() || '',
      );
    } catch (error: any) {
      // Transformar errores de API a errores de negocio
      if (error.message?.includes('Invalid token') || error.message?.includes('Token inválido')) {
        throw new Error('Token inválido o expirado');
      }
      if (error.message?.includes('expired') || error.message?.includes('expirado')) {
        throw new Error('La invitación ha expirado');
      }
      if (error.message?.includes('already accepted') || error.message?.includes('ya aceptada')) {
        throw new Error('Esta invitación ya fue aceptada');
      }
      if (error.message?.includes('User not found') || error.message?.includes('Usuario no encontrado')) {
        throw new Error('Usuario no encontrado');
      }
      throw error;
    }
  }

  /**
   * Obtener invitaciones pendientes del equipo
   * @returns Promise con array de invitaciones pendientes
   */
  async getPendingInvitations(): Promise<PendingInvitation[]> {
    try {
      return await this.apiAdapter.getPendingInvitations();
    } catch (error: any) {
      // Transformar errores de API a errores de negocio
      if (error.response?.status === 401) {
        throw new Error('Sesión expirada. Por favor inicia sesión nuevamente');
      }
      if (error.response?.status === 403) {
        throw new Error('No tienes permisos para ver invitaciones');
      }
      throw error;
    }
  }

  /**
   * Reenviar una invitación
   * @param invitationId - ID de la invitación a reenviar
   * @returns Promise con resultado de la operación
   */
  async resendInvitation(invitationId: number): Promise<{ success: boolean; message?: string }> {
    // Validaciones de negocio
    if (!invitationId || invitationId <= 0) {
      throw new Error('ID de invitación inválido');
    }

    try {
      return await this.apiAdapter.resendInvitation(invitationId);
    } catch (error: any) {
      // Transformar errores de API a errores de negocio
      if (error.response?.status === 400) {
        const message = error.response?.data?.message || 'No se puede reenviar una invitación ya aceptada';
        throw new Error(message);
      }
      if (error.response?.status === 404) {
        throw new Error('Invitación no encontrada');
      }
      if (error.response?.status === 403) {
        throw new Error('No tienes permisos para reenviar invitaciones');
      }
      throw error;
    }
  }

  /**
   * Cancelar una invitación
   * @param invitationId - ID de la invitación a cancelar
   * @returns Promise con resultado de la operación
   */
  async cancelInvitation(invitationId: number): Promise<{ success: boolean; message?: string }> {
    // Validaciones de negocio
    if (!invitationId || invitationId <= 0) {
      throw new Error('ID de invitación inválido');
    }

    try {
      return await this.apiAdapter.cancelInvitation(invitationId);
    } catch (error: any) {
      // Transformar errores de API a errores de negocio
      if (error.response?.status === 404) {
        throw new Error('Invitación no encontrada');
      }
      if (error.response?.status === 403) {
        throw new Error('No tienes permisos para cancelar invitaciones');
      }
      throw error;
    }
  }
}
